import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { BodyText, Button, Radio, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsDressingMode = (
  props: CustomizationDetailsDressingModeProps,
) => {
  const {
    dressingMode,
    maxDressingsPortions,
    isDressingModeChangeNotAllowed,
    hasNoSelectedDressing,
    isDisabled,
    setDressingModeToOnTheSide,
    setDressingModeToMixedIn,
    onAddDressing,
  } = props;

  const { formatMessage } = useIntl();
  const helperText = useHelperText(props);

  return (
    <View style={styles.container}>
      <BodyText sizeMatch={['22']} style={styles.title}>
        <FormattedMessage {...messages.title} />
      </BodyText>

      <Radio.Group gap={theme.spacing['2']} itemsPerRow={2}>
        <Radio.Item
          palette="spinach"
          value="onTheSide"
          style={styles.radioItem}
          isSelected={dressingMode === 'onTheSide'}
          isDisabled={isDisabled || hasNoSelectedDressing}
          onPress={setDressingModeToOnTheSide}
          label={formatMessage(messages.label, { mode: 'onTheSide' })}
          accessibilityLabel={formatMessage(messages.a11yLabel, { mode: 'onTheSide' })} // prettier-ignore
        />

        <Radio.Item
          palette="spinach"
          value="mixedIn"
          style={styles.radioItem}
          isSelected={dressingMode === 'mixedIn'}
          isDisabled={isDisabled || isDressingModeChangeNotAllowed}
          onPress={setDressingModeToMixedIn}
          label={formatMessage(messages.label, { mode: 'mixedIn' })}
          accessibilityLabel={formatMessage(messages.a11yLabel, { mode: 'mixedIn' })} // prettier-ignore
        />
      </Radio.Group>

      {helperText ? <BodyText sizeMatch={['14']}>{helperText}</BodyText> : null}

      {dressingMode === 'onTheSide' && !hasNoSelectedDressing ? (
        <BodyText sizeMatch={['22']} style={[styles.largerMargin]}>
          <FormattedMessage
            {...messages.helperTextOnTheSide}
            values={{ max_portions: maxDressingsPortions }}
          />
        </BodyText>
      ) : null}

      {hasNoSelectedDressing || dressingMode === 'mixedIn' ? (
        <BodyText sizeMatch={['22']} style={[styles.largerMargin]}>
          <FormattedMessage {...messages.yourDressings} />
        </BodyText>
      ) : null}

      {hasNoSelectedDressing ? (
        <Button
          size="large-wide"
          palette="kale"
          accessibilityRole="link"
          accessibilityLabel={formatMessage(messages.addDressing)}
          onPress={onAddDressing}
        >
          <FormattedMessage {...messages.addDressing} />
        </Button>
      ) : null}
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useHelperText = (props: CustomizationDetailsDressingModeProps) => {
  const {
    dressingMode,
    maxDressingsPortions,
    isDressingModeChangeNotAllowed,
    hasNoSelectedDressing,
    isDelivery,
    isOutpost,
  } = props;

  const { formatMessage } = useIntl();

  if (dressingMode === 'mixedIn' && !isDressingModeChangeNotAllowed) {
    return formatMessage(messages.helperTextMixedIn, {
      max_portions: maxDressingsPortions,
    });
  }

  if (isDelivery) {
    return formatMessage(messages.helperTextDelivery);
  }

  if (isOutpost) {
    return formatMessage(messages.helperTextOutpost);
  }

  if (isDressingModeChangeNotAllowed && !hasNoSelectedDressing) {
    return formatMessage(messages.helperTextCannotMix);
  }
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'How would you like your dressing?',
    description: 'Customization | Dressing details | Dressing mode title',
  },
  label: {
    defaultMessage: `{mode, select,
      onTheSide {On the side}
      mixedIn {Mixed in}
      other {}
    }`,
    description: 'Customization | Dressing details | Dressing mode label',
  },
  a11yLabel: {
    defaultMessage: `{mode, select,
      onTheSide {Select dressing on the side}
      mixedIn {Select dressing mixed in}
      other {}
    }`,
    description: 'Customization | Dressing details | Dressing mode a11y label',
  },
  helperTextOnTheSide: {
    defaultMessage: 'Select up to {max_portions} total portions:',
    description: 'Customization | Dressing details | On the side helper text',
  },
  helperTextMixedIn: {
    defaultMessage:
      'For optimal freshness, eat within 15 minutes of pickup time.',
    description: 'Customization | Dressing details | Mixed in helper text',
  },
  yourDressings: {
    defaultMessage: 'Your dressings',
    description: 'Customization | Dressing details | Your dressings',
  },
  addDressing: {
    defaultMessage: 'Add dressing',
    description: 'Customization | Title | Add dressing CTA',
  },
  helperTextCannotMix: {
    defaultMessage:
      'For optimal freshness, dressing for plates are served on the side.',
    description: 'Customization | Dressing details | Cannot mix helper text',
  },
  helperTextDelivery: {
    defaultMessage:
      'For optimal freshness, dressing for delivery orders are served on the side.',
    description: 'Customization | Dressing details | Delivery helper text',
  },
  helperTextOutpost: {
    defaultMessage:
      'For optimal freshness, dressing for outpost orders are served on the side.',
    description: 'Customization | Dressing details | Outpost helper text',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    rowGap: theme.spacing['2'],
  },
  title: {
    marginBottom: theme.spacing['2'],
  },
  largerMargin: {
    marginTop: theme.spacing['4'],
  },
  radioItem: {
    minHeight: 64,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationDetailsDressingModeProps = {
  dressingMode: 'onTheSide' | 'mixedIn' | string;
  maxDressingsPortions: number;
  isDisabled: boolean;
  isDressingModeChangeNotAllowed: boolean;
  isDelivery: boolean;
  isOutpost: boolean;
  hasNoSelectedDressing: boolean;
  setDressingModeToOnTheSide: () => void;
  setDressingModeToMixedIn: () => void;
  onAddDressing: () => void;
};
